import {
  getEffectiveApproachBeats,
  type CircleHitObject,
  type CirclesChart,
} from '@shared/circlesChart';
import type { CirclesEngineState } from '@shared/engine/circles/state';
import {
  GOOD_WINDOW_MS,
  OK_WINDOW_MS,
  PERFECT_WINDOW_MS,
} from '@shared/gameTypes';
import { bpmToBeatsPerMs } from '@shared/engine/circles/core';
import type { PlayfieldRect } from '../circlesPlayfieldGeometry';
import { kiaiScaleBoost } from './circlesDrawScene';
import type { HitObjectRenderer } from './circlesDrawHitObject';
import type { SliderRenderer } from './circlesDrawSlider';
import type { SpinnerRenderer } from './circlesDrawSpinner';
import { FollowpointRenderer } from './circlesDrawFollowpoints';
import { SLIDER_FADE_OUT_BEATS } from './circlesRenderConstants';
import type { RenderQuality } from '../../rendering/renderQuality';

/**
 * Subsystems the iteration dispatches to. Passed in by the
 * orchestrator so this module's class state stays minimal — only the
 * monotonic "first hit object still possibly visible" cursor, plus
 * the followpoint sub-renderer with its own matching cursor.
 */
export interface HitObjectPassDeps {
  hitObject: HitObjectRenderer;
  slider: SliderRenderer;
  spinner: SpinnerRenderer;
  dpr: number;
  reducedMotion: boolean;
  quality: RenderQuality;
}

/**
 * Hit-object render pass: cull to the visible window, draw the
 * followpoints under the circles, then back-to-front sort so the
 * smallest / most urgent approach ring renders on top. Spinners,
 * sliders, and circles dispatch to their respective subsystems.
 *
 * Owns a monotonic `firstVisibleIdx` cursor into a beat-sorted view
 * of `chart.hitObjects` so per-frame work collapses from
 * O(hitObjects) to O(visible). On a 220-object extreme chart at
 * 60 fps that turns ~13 k iterations/sec into ~600 — the dominant
 * per-frame win for long Circles maps on phones. Reset by
 * {@link reset} on loop wrap.
 *
 * Every chart-construction path today (imported osu! beatmaps, the
 * synthesised `circlesFromOnsets` builder, the in-app map editor)
 * already emits hit objects in beat-ascending order, and `validate
 * CirclesChart` walks a sorted copy, but the contract isn't pinned
 * down at the type level. To keep the cursor correctness independent
 * of that convention, we lazily re-sort per chart reference and
 * cache the result — same trick as `getStackOffsets` — so a future
 * tool that emits an unsorted array doesn't silently lose tiles.
 *
 * The window math (perfect / good / ok beat-windows, late cutoff,
 * post-hit visible tail) all happens inside {@link draw} so per-
 * circle dispatch stays cheap. Mirrors the engine's clickability
 * cutoff (`okWindowMs`) so circles stay drawn for the full duration
 * the engine still accepts a hit on them.
 */
export class HitObjectsPass {
  private firstVisibleIdx = 0;
  private followpoints = new FollowpointRenderer();
  /**
   * Most recently seen chart reference. When the renderer is fed a
   * new chart (loop swap, mode change), the next {@link draw} call
   * rebuilds {@link sortedHitObjects} and resets the cursor. One-
   * chart cache because the renderer plays one chart at a time.
   */
  private lastChart: CirclesChart | null = null;
  private sortedHitObjects: ReadonlyArray<CircleHitObject> = [];

  /**
   * Reset both cursors on loop wrap / chart swap. The renderer's
   * `clearLoopBoundFx` forwards here so a new playthrough doesn't
   * inherit a cursor pointing past the previous loop's tail.
   */
  reset(): void {
    this.firstVisibleIdx = 0;
    this.followpoints.reset();
  }

  draw(
    ctx: CanvasRenderingContext2D,
    chart: CirclesChart,
    state: CirclesEngineState,
    beat: number,
    field: PlayfieldRect,
    kiaiIdx: number,
    deps: HitObjectPassDeps,
  ): void {
    const approach = getEffectiveApproachBeats(chart);
    // Convert the engine's hit-window thresholds (in ms) into beats so
    // the renderer can express "is this circle currently in the
    // perfect/good window?" directly. Done once per frame so per-circle
    // draw is a cheap comparison.
    //
    // Reads `chart.perfectWindowMs` / `goodWindowMs` first so per-variant
    // tuning (Hard / Extreme have tighter windows) reflects in the
    // visual halo, falling back to the global Pulse constants for
    // charts that didn't override them.
    //
    // Floor matches `circles/core.ts` `HIT_HALF_BEAT_WINDOW_FALLBACK`
    // (`0.001`) so a degenerate BPM doesn't make the renderer's window
    // calc disagree with the engine's — without this clamp, very low
    // BPMs would let the engine accept hits while the renderer's
    // `windowProximity` ramp evaluated to zero and the circle never lit up.
    const beatsPerMs = bpmToBeatsPerMs(chart.bpm);
    const perfectMs = chart.perfectWindowMs ?? PERFECT_WINDOW_MS;
    const goodMs = chart.goodWindowMs ?? GOOD_WINDOW_MS;
    // OK band sets the engine's clickability cutoff (a tap inside
    // ±okWindowBeats still scores as a hit). The renderer must keep
    // circles visible through that whole window — at high BPM
    // `okWindowMs / msPerBeat` exceeds the historic 0.6-beat magic
    // cutoff, and players were able to score OKs on invisible
    // circles. We add a small post-late-edge tail so the resolved
    // sprite has a visible flash-out instead of popping.
    const okMs = Math.max(goodMs, chart.okWindowMs ?? OK_WINDOW_MS);
    const okWindowBeats = Math.max(okMs * beatsPerMs, 0.001);
    const perfectWindowBeats = Math.max(perfectMs * beatsPerMs, 0.001);
    const goodWindowBeats = Math.max(goodMs * beatsPerMs, 0.001);
    const POST_HIT_VISIBLE_BEATS = 0.2;
    const lateCutoff = -(okWindowBeats + POST_HIT_VISIBLE_BEATS);

    // Rebuild the sorted view on chart swap (one allocation per chart
    // load) — the cursor logic below depends on beat-ascending order.
    // We hold this independently of `chart.hitObjects` so a future
    // chart producer that emits in a different order doesn't silently
    // skip tiles.
    if (this.lastChart !== chart) {
      this.lastChart = chart;
      this.sortedHitObjects = [...chart.hitObjects].sort((a, b) => a.beat - b.beat);
      this.firstVisibleIdx = 0;
      this.followpoints.reset();
    }
    const hitObjects = this.sortedHitObjects;

    // Advance the cursor past hit objects that can NEVER come back
    // into view — their full lifetime (including slider end / spinner
    // end + fade-out tail) sits behind the current beat. Sorted-by-
    // beat input + monotonic cursor means the cumulative cost per
    // run is O(hitObjects), not O(hitObjects × frames).
    //
    // Conservative on the upper bound: we only advance past an object
    // once it's certain to never paint again. For sliders + spinners
    // that means accounting for their tail beats.
    while (this.firstVisibleIdx < hitObjects.length) {
      const obj = hitObjects[this.firstVisibleIdx];
      let lifetimeEnd = obj.beat - lateCutoff;
      if (obj.kind === 'slider' && obj.slider) {
        lifetimeEnd = obj.slider.endBeat + SLIDER_FADE_OUT_BEATS;
      } else if (obj.kind === 'spinner') {
        lifetimeEnd = (obj.spinnerEndBeat ?? obj.beat) + POST_HIT_VISIBLE_BEATS;
      }
      if (lifetimeEnd < beat) {
        this.firstVisibleIdx += 1;
      } else {
        break;
      }
    }

    // Cull to visible (within approach window and not past the late
    // cutoff). Sliders + spinners use their own end-beat as the late
    // cutoff so the body / spinner stays visible through its full
    // active window. Allocated per-frame because the visible set
    // changes constantly; cost is small at typical chart densities.
    const visibleCircles: { obj: CircleHitObject; t: number; dist: number }[] = [];
    for (let i = this.firstVisibleIdx; i < hitObjects.length; i += 1) {
      const obj = hitObjects[i];
      const dist = obj.beat - beat;
      if (dist > approach) {
        // Sorted by beat — future objects all sit past the approach
        // window from here, none of them are visible yet.
        break;
      }
      // Sliders need to remain visible AFTER the head is resolved —
      // the body keeps playing until `endBeat` (plus a brief fade
      // out). Skip the resolvedIds early-out for sliders that are
      // still in their active window.
      if (state.resolvedIds.has(obj.id)) {
        if (obj.kind !== 'slider' || !obj.slider) {
          continue;
        }
        if (beat > obj.slider.endBeat + SLIDER_FADE_OUT_BEATS) {
          continue;
        }
      }
      // Spinners are spinnable across `[obj.beat, spinnerEndBeat]`
      // (per `findActiveSpinner` / `expireDueAutoResolutions` in
      // `shared/engine/circles/core.ts`), so the renderer's late
      // cutoff for spinners tracks `spinnerEndBeat` — circles use
      // the OK window because that's their clickability cutoff.
      // `POST_HIT_VISIBLE_BEATS` is a safety margin: the renderer
      // can read state a fraction of a beat ahead of the engine's
      // last step, and the tail keeps the spinner from popping out
      // a frame before `resolvedIds` registers the resolution.
      let objectLateCutoff: number;
      if (obj.kind === 'spinner') {
        objectLateCutoff =
          -((obj.spinnerEndBeat ?? obj.beat) - obj.beat) - POST_HIT_VISIBLE_BEATS;
      } else if (obj.kind === 'slider' && obj.slider) {
        objectLateCutoff = -(obj.slider.endBeat - obj.beat) - SLIDER_FADE_OUT_BEATS;
      } else {
        objectLateCutoff = lateCutoff;
      }
      if (dist < objectLateCutoff) {
        continue;
      }
      visibleCircles.push({ obj, t: dist / approach, dist });
    }
    // Followpoints render BEFORE the circles so the dots sit under
    // the approach ring — the guide should never cover or interrupt
    // the hit object the player is reading. Shares the same beat-
    // sorted view as the main pass so its monotonic cursor is sound.
    this.followpoints.draw(
      ctx,
      chart,
      hitObjects,
      state,
      beat,
      approach,
      okWindowBeats,
      field,
    );

    // Back-to-front draw order. Default rule (osu! convention): draw
    // furthest-first so the smallest, most imminent ring renders last /
    // on top — and a circle the player is LATE on (t just below 0, still
    // inside its click window) stays on top too, since it's the most
    // urgent thing to read.
    //
    // The one exception: an actively-held slider (head already passed,
    // t < 0, opaque body still on screen) sinks to the BOTTOM. Otherwise
    // its body paints over a circle approaching at its tail (a classic
    // osu! pattern — circle stacked on a slider end) and buries the very
    // circle the player is about to hit. We single out slider *bodies*
    // rather than all past-beat objects so late plain circles keep their
    // on-top urgency.
    visibleCircles.sort((a, b) => {
      const aSliderBody = a.obj.kind === 'slider' && a.t < 0;
      const bSliderBody = b.obj.kind === 'slider' && b.t < 0;
      if (aSliderBody !== bSliderBody) {
        return aSliderBody ? -1 : 1;
      }
      return b.t - a.t;
    });

    // Kiai scale boost is computed once per frame: it's identical
    // across every circle on screen, so we don't recompute the envelope
    // per circle. Driver feeds straight into the hit-object/slider
    // renderers as a multiplier on the radius.
    const scaleBoost = kiaiScaleBoost(kiaiIdx, beat, deps.reducedMotion);

    for (const { obj, t, dist } of visibleCircles) {
      if (obj.kind === 'spinner') {
        deps.spinner.draw(ctx, chart, obj, beat, field, state);
      } else if (obj.kind === 'slider' && obj.slider) {
        deps.slider.drawSlider(
          ctx,
          chart,
          obj,
          t,
          field,
          beat,
          dist,
          perfectWindowBeats,
          goodWindowBeats,
          scaleBoost,
          state,
          deps.dpr,
          deps.hitObject,
          deps.quality,
        );
      } else {
        deps.hitObject.drawCircle(
          ctx,
          chart,
          obj,
          t,
          field,
          dist,
          perfectWindowBeats,
          goodWindowBeats,
          scaleBoost,
          deps.quality,
        );
      }
    }
  }
}
